// =============================================================================
// VulnTracker — Vulnerabilities Page (Component-Driven via Threat Intel)
// =============================================================================
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useProjects, useComponents, useVulnerability, useForceScanComponent, useComponentVulnerabilities, useUpdateMatchStatus } from '@/api/hooks';
import type { ComponentVulnerability } from '@/api/hooks';
import { SeverityBadge } from '@/components/SeverityBadge';
import type { Severity, Vulnerability, Project, Component } from '@/types';
import {
  ShieldExclamationIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  XMarkIcon,
  LinkIcon,
  InformationCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

export function VulnerabilitiesPage() {
  // ── State ──
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedComponentId, setSelectedComponentId] = useState<string>('');
  const [detailVulnId, setDetailVulnId] = useState<string | null>(null);

  // ── Data fetching ──
  const { data: projectsData, isLoading: loadingProjects } = useProjects({ page: 1, page_size: 100 });
  const { data: componentsData, isLoading: loadingComponents } = useComponents(
    { project_id: selectedProjectId, page: 1, page_size: 500 },
    { enabled: !!selectedProjectId },
  );

  // Auto-fetch stored vulnerabilities from DB when component is selected
  const { data: storedVulnsData, isLoading: loadingStoredVulns } = useComponentVulnerabilities(
    selectedComponentId || null,
  );

  // Force-scan mutation for "Scan for New Vulnerabilities" button
  const forceScanMutation = useForceScanComponent();

  const projects: Project[] = projectsData?.items ?? [];
  const components: Component[] = componentsData?.items ?? [];

  // Merge: show stored DB vulns by default; after a force-scan, show the updated results
  const storedVulnerabilities: ComponentVulnerability[] = storedVulnsData?.items ?? [];
  const scannedVulnerabilities: ComponentVulnerability[] = forceScanMutation.data?.items ?? [];

  // If user has clicked "Scan" and got fresh results, show those; otherwise show stored.
  // Filter out pure security-advisory entries (CLSA-*, ALBA-*, ALEA-*, etc.) that carry
  // no CVE ID and no GHSA ID — these come via OSV but are vendor advisories, not CVEs.
  const _raw: ComponentVulnerability[] =
    forceScanMutation.isSuccess ? scannedVulnerabilities : storedVulnerabilities;
  const vulnerabilities: ComponentVulnerability[] =
    _raw.filter((v) => !!(v.cve_id || v.ghsa_id));

  const scanSource: string | null =
    forceScanMutation.isSuccess
      ? (forceScanMutation.data?.source ?? null)
      : storedVulnerabilities.length > 0
        ? 'database'
        : null;

  const hasStoredData = storedVulnerabilities.length > 0;
  const isScanning = forceScanMutation.isPending;

  // ── Auto-select preferred project ("Demo Project") or fall back to first ──
  // Dep: [projects] only — selectedProjectId is intentionally excluded to
  // avoid firing this effect after the state it sets changes (loop risk).
  useEffect(() => {
    if (!selectedProjectId && projects.length > 0) {
      const preferred = projects.find((p) => p.name.toLowerCase() === 'demo project');
      setSelectedProjectId(preferred ? preferred.id : projects[0].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  // ── Reset component & scan results when project changes ──
  useEffect(() => {
    setSelectedComponentId('');
    forceScanMutation.reset();
  }, [selectedProjectId]);

  // ── Clear force-scan results when component selection changes (stored vulns auto-refresh) ──
  useEffect(() => {
    forceScanMutation.reset();
  }, [selectedComponentId]);

  // ── Auto-select preferred component (log4j-core → django → first) ──
  // Dep: [components] only — selectedComponentId is intentionally excluded to
  // avoid firing this effect after the state it sets changes (loop risk).
  useEffect(() => {
    if (!selectedComponentId && components.length > 0) {
      const preferred =
        components.find((c) => c.name.toLowerCase() === 'log4j-core') ||
        components.find((c) => c.name.toLowerCase() === 'django' && c.version?.startsWith('3.')) ||
        components.find((c) => c.ecosystem !== 'other') ||
        components[0];
      setSelectedComponentId(preferred.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components]);

  // ── Severity stats ──
  const severityCounts = vulnerabilities.reduce(
    (acc, v) => {
      const s = v.severity?.toLowerCase() ?? 'unknown';
      if (s === 'critical') acc.critical++;
      else if (s === 'high') acc.high++;
      else if (s === 'medium') acc.medium++;
      else if (s === 'low') acc.low++;
      else if (s === 'none' || s === 'unknown') acc.none++;
      return acc;
    },
    { critical: 0, high: 0, medium: 0, low: 0, none: 0 },
  );

  const selectedComponent = components.find((c) => c.id === selectedComponentId);

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vulnerabilities</h1>
          <p className="mt-1 text-sm text-gray-500">
            Scan components against threat intel to discover known vulnerabilities
          </p>
        </div>
      </div>

      {/* ── Selectors Row ── */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          {/* Project Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project
            </label>
            {loadingProjects ? (
              <div className="h-10 bg-gray-100 animate-pulse rounded-md" />
            ) : projects.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No projects yet</p>
            ) : (
              <select
                className="input-field px-3"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
              >
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Component Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Component
            </label>
            {loadingComponents ? (
              <div className="h-10 bg-gray-100 animate-pulse rounded-md" />
            ) : components.length === 0 ? (
              <p className="text-sm text-gray-400 italic">
                {selectedProjectId ? 'No components in this project' : 'Select a project first'}
              </p>
            ) : (
              <select
                className="input-field px-3"
                value={selectedComponentId}
                onChange={(e) => setSelectedComponentId(e.target.value)}
              >
                {[...components].sort((a, b) => a.name.localeCompare(b.name)).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}@{c.version} ({c.ecosystem})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Scan Button */}
          <div>
            <button
              onClick={() => {
                if (selectedComponentId) {
                  forceScanMutation.mutate(selectedComponentId);
                }
              }}
              disabled={!selectedComponentId || isScanning}
              className="btn btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isScanning ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Scanning…
                </>
              ) : (
                <>
                  <ArrowPathIcon className="h-5 w-5" />
                  Scan for New Vulnerabilities
                </>
              )}
            </button>
          </div>

        </div>

        {/* ── Source Indicator ── */}
        {selectedComponentId && scanSource && (
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
            <InformationCircleIcon className="h-4 w-4" />
            {scanSource === 'database' ? (
              <span>Showing <strong>{vulnerabilities.length}</strong> stored vulnerabilities from database. Click <em>Scan for New Vulnerabilities</em> to query live threat intel sources.</span>
            ) : (
              <span>Showing <strong>{vulnerabilities.length}</strong> results from live scan ({scanSource}). Results have been saved to database.</span>
            )}
          </div>
        )}
      </div>

      {/* ── Severity Summary Cards ── */}
      {selectedComponentId && vulnerabilities.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <SeveritySummaryCard label="Critical" count={severityCounts.critical} color="red" />
          <SeveritySummaryCard label="High" count={severityCounts.high} color="orange" />
          <SeveritySummaryCard label="Medium" count={severityCounts.medium} color="yellow" />
          <SeveritySummaryCard label="Low" count={severityCounts.low} color="blue" />
          <SeveritySummaryCard label="None" count={severityCounts.none} color="gray" />
        </div>
      )}

      {/* ── Vulnerabilities Table ── */}
      <div className="card overflow-hidden p-0">
        {!selectedComponentId ? (
          <EmptyState
            icon={<ShieldExclamationIcon className="h-12 w-12 text-gray-300" />}
            title="Select a component"
            description="Choose a project and component above to view known vulnerabilities."
          />
        ) : loadingStoredVulns ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            <span className="ml-3 text-sm text-gray-500">Loading stored vulnerabilities…</span>
          </div>
        ) : isScanning ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            <span className="ml-3 text-sm text-gray-500">Scanning external sources for new vulnerabilities…</span>
          </div>
        ) : vulnerabilities.length === 0 ? (
          <EmptyState
            icon={<CheckCircleIcon className="h-12 w-12 text-green-300" />}
            title="No vulnerabilities found"
            description={
              selectedComponent
                ? `No known vulnerabilities found for ${selectedComponent.name}@${selectedComponent.version}. Click "Scan for New Vulnerabilities" to check live threat intel sources.`
                : 'No vulnerabilities found. Try scanning for new ones.'
            }
          />
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CVSS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CWE
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reachability
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Published
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {vulnerabilities.map((v) => (
                <tr key={v.match_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-700">
                    {v.cve_id || v.ghsa_id || v.osv_id || v.vulnerability_id.slice(0, 8)}
                  </td>
                  <td className="px-6 py-4 max-w-sm truncate text-sm text-gray-900">
                    {v.title}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <SeverityBadge severity={v.severity as Severity} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {v.cvss_v3_score?.toFixed(1) ?? '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {v.cwe_ids && v.cwe_ids.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {v.cwe_ids.slice(0, 3).map((cwe) => (
                          <a
                            key={cwe}
                            href={`https://cwe.mitre.org/data/definitions/${cwe.replace('CWE-', '')}.html`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:underline"
                          >
                            {cwe}
                          </a>
                        ))}
                        {v.cwe_ids.length > 3 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            +{v.cwe_ids.length - 3}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ReachabilityBadge reachability={v.reachability} epssScore={v.epss_score} inKev={v.in_kev} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusSelect matchId={String(v.match_id)} current={v.remediation_status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {v.published_at
                      ? new Date(v.published_at).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => setDetailVulnId(v.vulnerability_id)}
                      className="text-primary-600 hover:text-primary-800 hover:underline inline-flex items-center gap-1"
                      title="View vulnerability details"
                    >
                      <InformationCircleIcon className="h-4 w-4" />
                      <span>View</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Vulnerability Detail Modal ── */}
      {detailVulnId && (
        <VulnerabilityDetailModal
          vulnerabilityId={detailVulnId}
          onClose={() => setDetailVulnId(null)}
        />
      )}
    </div>
  );
}

// ── Vulnerability Detail Modal ───────────────────────────────────────────

function VulnerabilityDetailModal({
  vulnerabilityId,
  onClose,
}: {
  vulnerabilityId: string;
  onClose: () => void;
}) {
  const { data: vuln, isLoading, isError } = useVulnerability(vulnerabilityId);

  // Close on Escape key — dep array is empty: onClose is an inline arrow
  // function that changes reference on every parent render; including it would
  // cause this effect (and the addEventListener) to fire on every parent
  // re-render, creating a continuous re-registration loop.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayId = vuln?.cve_id || vuln?.ghsa_id || vuln?.osv_id || vulnerabilityId.slice(0, 8);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      {/* Modal panel */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl rounded-xl bg-white shadow-2xl ring-1 ring-gray-200">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <ShieldExclamationIcon className="h-6 w-6 text-red-500" />
              <h2 className="text-lg font-semibold text-gray-900">
                {isLoading ? 'Loading…' : displayId}
              </h2>
              {vuln && <SeverityBadge severity={vuln.severity as Severity} />}
            </div>
            <button
              onClick={onClose}
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="max-h-[70vh] overflow-y-auto px-6 py-5 space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : isError ? (
              <div className="text-center py-12 text-red-600">
                Failed to load vulnerability details. Please try again.
              </div>
            ) : vuln ? (
              <>
                {/* Title */}
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{vuln.title}</h3>
                </div>

                {/* Key Facts Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <DetailField label="CVSS v3 Score" value={vuln.cvss_v3_score != null ? String(vuln.cvss_v3_score.toFixed(1)) : '—'} />
                  <DetailField label="Severity" value={vuln.severity.toUpperCase()} />
                  <DetailField label="Source" value={vuln.source.toUpperCase()} />
                  <DetailField label="Status" value={vuln.status.replace(/_/g, ' ').toUpperCase()} />
                </div>

                {/* IDs */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {vuln.cve_id && <DetailField label="CVE ID" value={vuln.cve_id} />}
                  {vuln.ghsa_id && <DetailField label="GHSA ID" value={vuln.ghsa_id} />}
                  {vuln.osv_id && <DetailField label="OSV ID" value={vuln.osv_id} />}
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <DetailField label="Published" value={vuln.published_at ? new Date(vuln.published_at).toLocaleDateString() : '—'} />
                  <DetailField label="Modified" value={vuln.modified_at ? new Date(vuln.modified_at).toLocaleDateString() : '—'} />
                  <DetailField label="Tracked Since" value={new Date(vuln.created_at).toLocaleDateString()} />
                </div>

                {/* CVSS Vector */}
                {vuln.cvss_v3_vector && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">CVSS v3 Vector</p>
                    <code className="block text-sm bg-gray-50 rounded-md px-3 py-2 text-gray-700 break-all">
                      {vuln.cvss_v3_vector}
                    </code>
                  </div>
                )}

                {/* Flags */}
                <div className="flex gap-4">
                  <FlagBadge label="Exploit Available" active={vuln.exploit_available} />
                  <FlagBadge label="Patch Available" active={vuln.patch_available} />
                  <FlagBadge label="CISA KEV" active={vuln.in_kev ?? false} />
                </div>

                {/* EPSS Score */}
                {vuln.epss_score != null && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <DetailField label="EPSS Score" value={`${(vuln.epss_score * 100).toFixed(1)}%`} />
                    <DetailField label="EPSS Percentile" value={vuln.epss_percentile != null ? `${(vuln.epss_percentile * 100).toFixed(0)}th` : '—'} />
                  </div>
                )}

                {/* CWE IDs */}
                {vuln.cwe_ids && vuln.cwe_ids.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">CWE Weaknesses</p>
                    <div className="flex flex-wrap gap-2">
                      {vuln.cwe_ids.map((cwe) => (
                        <a
                          key={cwe}
                          href={`https://cwe.mitre.org/data/definitions/${cwe.replace('CWE-', '')}.html`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-medium text-purple-700 hover:bg-purple-100 transition-colors"
                        >
                          {cwe}
                          <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Description</p>
                  <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 rounded-md p-4 max-h-48 overflow-y-auto">
                    {vuln.description || 'No description available.'}
                  </div>
                </div>

                {/* References */}
                {vuln.references && vuln.references.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">References</p>
                    <ul className="space-y-1.5">
                      {vuln.references.map((ref, i) => (
                        <li key={i}>
                          <a
                            href={ref.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-800 hover:underline break-all"
                          >
                            <LinkIcon className="h-3.5 w-3.5 flex-shrink-0" />
                            <span>{ref.url}</span>
                            {ref.type && ref.type !== 'WEB' && (
                              <span className="ml-1 text-xs text-gray-400">({ref.type})</span>
                            )}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Source URL */}
                {vuln.source_url && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Original Source</p>
                    <a
                      href={vuln.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-800 hover:underline"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      {vuln.source_url}
                    </a>
                  </div>
                )}
              </>
            ) : null}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-3 flex justify-end">
            <button
              onClick={onClose}
              className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-gray-900">{value}</p>
    </div>
  );
}

function FlagBadge({ label, active }: { label: string; active: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
        active ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {active ? '⚠ ' : '✓ '}{label}
    </span>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function EmptyState({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      {icon}
      <h3 className="mt-3 text-lg font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500 max-w-md">{description}</p>
    </div>
  );
}

function SeveritySummaryCard({
  label,
  count,
  color,
}: {
  label: string;
  count: number;
  color: 'red' | 'orange' | 'yellow' | 'blue' | 'gray';
}) {
  const colorMap = {
    red: 'bg-red-50 text-red-800 border-red-200',
    orange: 'bg-orange-50 text-orange-800 border-orange-200',
    yellow: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
  };
  return (
    <div className={`rounded-lg border p-4 text-center ${colorMap[color]}`}>
      <p className="text-2xl font-bold">{count}</p>
      <p className="text-sm">{label}</p>
    </div>
  );
}

function ReachabilityBadge({
  reachability,
  epssScore,
  inKev,
}: {
  reachability: string;
  epssScore: number | null;
  inKev: boolean;
}) {
  const level = (reachability ?? 'unknown').toLowerCase();
  const colorMap: Record<string, string> = {
    reachable: 'bg-red-100 text-red-800 border-red-300',
    not_reachable: 'bg-green-100 text-green-800 border-green-300',
    highly_probable: 'bg-orange-100 text-orange-800 border-orange-300',
    probable: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    unlikely: 'bg-blue-100 text-blue-700 border-blue-300',
    unknown: 'bg-gray-100 text-gray-600 border-gray-300',
  };
  const labelMap: Record<string, string> = {
    reachable: 'Reachable',
    not_reachable: 'Not Reachable',
    highly_probable: 'Highly Probable',
    probable: 'Probable',
    unlikely: 'Unlikely',
    unknown: 'Unknown',
  };

  const tooltip = [
    epssScore !== null ? `EPSS: ${(epssScore * 100).toFixed(1)}%` : null,
    inKev ? '⚠ CISA KEV' : null,
  ]
    .filter(Boolean)
    .join(' · ') || 'No EPSS data';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${colorMap[level] ?? colorMap.unknown}`}
      title={tooltip}
    >
      {inKev && <span className="text-red-600">⚡</span>}
      {level === 'reachable' && <span>✔</span>}
      {level === 'not_reachable' && <span>✕</span>}
      {labelMap[level] ?? 'Unknown'}
    </span>
  );
}

function StatusSelect({ matchId, current }: { matchId: string; current: string }) {
  const [value, setValue] = useState(current);
  const { mutateAsync, isPending } = useUpdateMatchStatus();

  useEffect(() => { setValue(current); }, [current]);

  const OPTIONS = [
    { value: 'not_started', label: 'Not Started' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'deferred', label: 'Deferred' },
    { value: 'accepted_risk', label: 'Accepted Risk' },
  ] as const;

  const colorMap: Record<string, string> = {
    not_started: 'bg-gray-100 text-gray-700 border-gray-300',
    in_progress: 'bg-blue-100 text-blue-700 border-blue-300',
    completed: 'bg-green-100 text-green-700 border-green-300',
    deferred: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    accepted_risk: 'bg-orange-100 text-orange-700 border-orange-300',
  };

  async function handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newValue = e.target.value;
    const prev = value;
    setValue(newValue);
    try {
      await mutateAsync({ matchId, data: { remediation_status: newValue } });
    } catch (err: unknown) {
      setValue(prev);
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to update status';
      toast.error(msg);
    }
  }

  return (
    <select
      value={value}
      onChange={handleChange}
      disabled={isPending}
      className={`text-xs font-medium rounded-full px-2.5 py-0.5 border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${colorMap[value] ?? 'bg-gray-100 text-gray-700 border-gray-300'}`}
    >
      {OPTIONS.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function RemediationBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    not_started: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    deferred: 'bg-yellow-100 text-yellow-700',
    accepted_risk: 'bg-orange-100 text-orange-700',
  };
  const labelMap: Record<string, string> = {
    not_started: 'Not Started',
    in_progress: 'In Progress',
    completed: 'Completed',
    deferred: 'Deferred',
    accepted_risk: 'Accepted Risk',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colorMap[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {labelMap[status] ?? status}
    </span>
  );
}

function SourceBadge({ source }: { source: string | null }) {
  const colorMap: Record<string, string> = {
    osv: 'bg-indigo-100 text-indigo-700',
    nvd: 'bg-emerald-100 text-emerald-700',
    github: 'bg-purple-100 text-purple-700',
    vendor: 'bg-cyan-100 text-cyan-700',
    manual: 'bg-gray-100 text-gray-700',
  };
  const labelMap: Record<string, string> = {
    osv: 'OSV.dev',
    nvd: 'NVD',
    github: 'GitHub',
    vendor: 'Vendor',
    manual: 'Manual',
  };
  const key = (source ?? 'unknown').toLowerCase();
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colorMap[key] ?? 'bg-gray-100 text-gray-700'}`}>
      {labelMap[key] ?? source ?? '—'}
    </span>
  );
}
